import { ComponentPropsWithRef, FC, Fragment } from "react";
import { twMerge } from "tailwind-merge";

interface ITableDataLoading {
    columns?: number;
    rows?: number;
}

export const TableDataLoading: FC<ITableDataLoading> = ({
    columns = 6,
    rows = 7,
}) => {
    return (
        <Fragment>
            {[...Array(rows)].map((_, i) => 
                <tr className="bg-white border-b border-zinc-200 animate-pulse" key={i}>
                    {
                        [...Array(columns)].map((_, j) =>
                            <td className="p-4" key={j}>
                                <div className="w-full h-5 bg-zinc-100 rounded"></div>
                            </td>
                        )
                    }
                </tr>
            )}
        </Fragment>
    );
};

export const TableEmpty = () => {

    return (
        <tr className="text-center">
            <td className="px-4 py-8 whitespace-pre-line text-zinc-400 h-72" colSpan={100}>
                <svg className="w-14 h-14 mx-auto mb-2" viewBox="0 0 24 24">
                    <g fill="none" stroke="currentColor" strokeWidth="1.2">
                        <path strokeLinecap="round" d="M14 4c3.771 0 5.657 0 6.828 1.172C22 6.343 22 8.229 22 12v1M10 4C6.229 4 4.343 4 3.172 5.172C2 6.343 2 8.229 2 12c0 3.771 0 5.657 1.172 6.828C4.343 20 6.229 20 10 20h3m-3-4H6"/>
                        <circle cx="18" cy="17" r="3"/>
                        <path strokeLinecap="round" d="m20.5 19.5l1 1M2 10h5m15 0H11"/>
                    </g>
                </svg>

                <span className="block text-center">No results found.</span>
            </td>
        </tr>
    )
}

const TableCell = ({
    className,
    ref,
    ...extra
}: ComponentPropsWithRef<"td">) => {
    return (
        <td
            ref={ref}
            className={twMerge(
                "px-6 py-3.5 align-middle text-xs",
                className
            )}
            {...extra}
        />
    );
};

const TableHead = ({
    className,
    ref,
    ...extra
}: ComponentPropsWithRef<"th">) => {
    return (
        <th
            ref={ref}
            className={twMerge(
                "px-6 text-left text-xs text-secondary font-medium capitalize [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
                className
            )}
            {...extra}
        />
    );
};

const TableRow = ({
    className,
    ref,
    ...extra
}: ComponentPropsWithRef<"tr">) => {
    return (
        <tr
            className={twMerge(
                "relative border-b border-b-secondary-50 transition-colors hover:bg-zinc-50",
                className
            )}
            ref={ref}
            {...extra}
        />
    );
};

const TableHeader = ({
    className,
    ref,
    ...extra
}: ComponentPropsWithRef<"thead">) => {
    return (
        <thead
            ref={ref}
            className={twMerge(
                "border-b border-b-secondary-50 bg-[#F5F2F0] capitalize [&_th]:py-3",
                className
            )}
            {...extra}
        />
    );
};

const TableBody = ({
    className,
    ref,
    ...extra
}: ComponentPropsWithRef<"tbody">) => {
    return (
        <tbody
            ref={ref}
            className={twMerge("[&_tr:last-child]:border-0", className)}
            {...extra}
        />
    );
};

// const TableCaption = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
//     ({ className, ...props }, ref) => (
//         <caption
//             ref={ref}
//             className={cn("text-muted-foreground mt-4 text-sm", className)}
//             {...props}
//         />
//     ),
// );

// TableCaption.displayName = "TableCaption";

const TableFooter = ({
    className,
    ref,
    ...extra
}: ComponentPropsWithRef<"tfoot">) => {
    return (
        <tfoot
            ref={ref}
            className={twMerge(
                "border-t bg-white border-secondary-50 font-medium [&>tr]:last:border-b-0",
                className,
            )}
            {...extra}
        />
    )
};

const Table = ({
    className,
    ref,
    ...extra
}: ComponentPropsWithRef<"table">) => {
    return (
        <div className="w-full bg-white">
            <table
                ref={ref}
                className={twMerge("w-full caption-bottom text-sm", className)}
                {...extra}
            />
        </div>
    );
};

export interface ITableContainer {
    children: React.ReactNode;
    className?: string
}

const TableContainer = (props: ITableContainer) => {
    return (
        <div
            className={twMerge("rounded-lg shadow-xs overflow-hidden", props.className)}
        >
            {props.children}
        </div>
    );
};

// Assign the static properties
Table.Cell = TableCell;
Table.Head = TableHead;
Table.Row = TableRow;
Table.Header = TableHeader;
Table.Body = TableBody;
Table.Footer = TableFooter;
Table.Empty = TableEmpty;

Table.Loading = TableDataLoading;
Table.Container = TableContainer;

export default Table;